import { useMemo } from 'react';
import { useAsync } from 'react-use';
import { useApi, configApiRef } from '@backstage/core-plugin-api';
import { useEntity } from '@backstage/plugin-catalog-react';
import { UnprocessedEntity } from '@backstage/plugin-catalog-unprocessed-entities-common';
import { catalogUnprocessedEntitiesApiRef } from '@backstage/plugin-catalog-unprocessed-entities';

const getOwnerRef = (owner: string) => {
  const parts = owner.split(':');
  const type = parts.length > 1 ? parts[0] : 'group';
  const rest = parts[1] || owner;
  const restParts = rest.split('/');
  const namespace = restParts.length > 1 ? restParts[0] : 'default';
  const name = restParts.length > 1 ? restParts.slice(1).join('/') : rest;

  return `${type}:${namespace}/${name}`;
};
type ProcessedUnprocessedEntity = UnprocessedEntity & {
  location_key: string | undefined;
};

export const useUnprocessedEntities = (): {
  loading: boolean;
  error: Error | undefined;
  unprocessedItems: ProcessedUnprocessedEntity[] | undefined;
} => {
  const { entity: entityPageGroup } = useEntity();
  const unprocessedApi = useApi(catalogUnprocessedEntitiesApiRef);
  const configApi = useApi(configApiRef);
  const rbacSuperUsers = useMemo(
    () =>
      new Set(
        (
          configApi.getOptionalConfigArray(
            'permission.rbac.admin.superUsers',
          ) ?? []
        ).map(c => c.getString('name')),
      ),
    [configApi],
  );

  // The "entity group page we are on" owner
  const entityPageGroupRef = `group:${
    entityPageGroup.metadata.namespace ?? 'default'
  }/${entityPageGroup?.metadata?.name ?? ''}`;

  const {
    loading,
    error,
    value: unprocessedItems,
  } = useAsync(async () => {
    const failedUnprocessed = await unprocessedApi.failed();

    const mapped = (failedUnprocessed?.entities ?? []).map(
      (failed: UnprocessedEntity) => {
        const rawOwner = failed.unprocessed_entity?.spec?.owner;
        const ownerStr = typeof rawOwner === 'string' ? rawOwner : '';
        const itemOwnerRef = getOwnerRef(ownerStr.trim());

        const itemIsSameAsEntityPageGroup = itemOwnerRef === entityPageGroupRef;
        const itemIsUnassigned =
          itemOwnerRef === 'group:default/' ||
          itemOwnerRef.startsWith('group:default/$') ||
          itemOwnerRef.includes('{{');

        const groupShouldBeShown =
          itemIsSameAsEntityPageGroup ||
          (itemIsUnassigned && rbacSuperUsers.has(entityPageGroupRef));

        const locationKeyMatch = /^url:(.*)$/.exec(failed.location_key ?? '');
        const locationKey = locationKeyMatch
          ? locationKeyMatch[1]
          : failed.location_key;

        return groupShouldBeShown
          ? {
              ...failed,
              location_key: locationKey,
            }
          : null;
      },
    );

    return mapped.filter(
      (item): item is ProcessedUnprocessedEntity => item !== null,
    );
  }, [entityPageGroupRef, unprocessedApi, rbacSuperUsers]);

  return {
    loading,
    error,
    unprocessedItems,
  };
};
